import React, { useEffect, useState } from "react";
import { X } from 'lucide-react';
import { adminApi } from "../../../config";
import { updateDetails } from "../profileAPIs";
import toast, { Toaster } from "react-hot-toast";

interface professionParams {
    skill: string[];
    experience: string;
    certification: string[];
    onUpdate?: (data: { skills: string[], experience: string, certifications: string[] }) => void;
}

export default function InstructorProfession({skill, experience, certification, onUpdate}: professionParams) {
    const [certifications, setCertifications] = useState<string[]>(certification || []);
    const [experienceLevel, setExperienceLevel] = useState<string>(experience || "Beginner");
    const [skills, setSkills] = useState<string[]>(skill || []);
    const [newSkill, setNewSkill] = useState<string>("");
    const [newCertification, setNewCertification] = useState<string>("");

    // Initialize state with props when component mounts or props change
    useEffect(() => {
        setCertifications(certification || []);
        setExperienceLevel(experience || "Beginner");
        setSkills(skill || []);
        
    }, [skill, experience, certification]);

    const handleAddSkill = () => {
        if (newSkill.trim() && !skills.includes(newSkill.trim())) {
            setSkills([...skills, newSkill.trim()]);
            setNewSkill("");
        }
    };

    const handleRemoveSkill = (skillToRemove: string) => {
        setSkills(skills.filter(skill => skill !== skillToRemove));
    };

    const handleAddCertification = () => {
        if (newCertification.trim() && !certifications.includes(newCertification.trim())) {
            setCertifications([...certifications, newCertification.trim()]);
            setNewCertification("");
        }
    };

    const handleRemoveCertification = (certToRemove: string) => {
        setCertifications(certifications.filter(cert => cert !== certToRemove));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, action: 'skill' | 'cert') => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (action === 'skill') {
                handleAddSkill();
            } else {
                handleAddCertification();
            }
        }
    };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const form = new FormData();
        form.append("skills", JSON.stringify(skills));
        form.append("certification", JSON.stringify(certifications));
        form.append("experience", experienceLevel);

        const api = await updateDetails(form) as { data?: { success?: boolean } };
        if (api.data?.success) {
            toast.success("Profile updated successfully");
        }
        // Call the onUpdate callback if provided
        if (onUpdate) {
            onUpdate({ skills, certifications, experience: experienceLevel });
        }
    };

    return (
        <div className="flex-1">
            <Toaster />
            <div className="bg-white rounded-2xl shadow-sm p-8">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-xl font-bold text-gray-900">Professional Details</h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Experience Level */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Experience Level
                        </label>
                        <select
                            value={experienceLevel}
                            onChange={(e) => setExperienceLevel(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                        >
                            <option value="(0-1 yrs.) Beginner">Beginner (0-1 years)</option>
                            <option value="(1-3 yrs.) Intermediate">Intermediate (1-3 years)</option>
                            <option value="(3-5 yrs.) Advanced">Advanced (3-5 years)</option>
                            <option value="(5+ yrs.) Expert">Expert (5+ years)</option>
                        </select>
                    </div>

                    {/* Skills */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Skills & Specializations
                        </label>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {skills.map((skill, index) => (
                                <span
                                    key={index}
                                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium flex items-center gap-2"
                                >
                                    {skill}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveSkill(skill)}
                                        className="text-purple-500 hover:text-red-600 transition-colors"
                                    >
                                        <X size={14} />
                                    </button>
                                </span>
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={newSkill}
                                onChange={(e) => setNewSkill(e.target.value)}
                                placeholder="Add a skill (e.g., Hatha Yoga, Meditation, etc.)"
                                className="flex-grow px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                                onKeyDown={(e) => handleKeyDown(e, 'skill')}
                            />
                            <button
                                type="button"
                                onClick={handleAddSkill}
                                disabled={!newSkill.trim()}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                                Add
                            </button>
                        </div>
                    </div>

                    {/* Certifications */}
                    {/* <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Certifications
                        </label>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {certifications.map((cert, index) => (
                                <span
                                    key={index}
                                    className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-2"
                                >
                                    {cert}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveCertification(cert)}
                                        className="text-green-500 hover:text-red-600 transition-colors"
                                    >
                                        <X size={14} />
                                    </button>
                                </span>
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={newCertification}
                                onChange={(e) => setNewCertification(e.target.value)}
                                placeholder="Add certification (e.g., RYT 200, CPR Certified, etc.)"
                                className="flex-grow px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                                onKeyDown={(e) => handleKeyDown(e, 'cert')} />
                            <button
                                type="button"
                                onClick={handleAddCertification}
                                disabled={!newCertification.trim()}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
                                Add
                            </button>
                        </div>
                    </div> */}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
                        >
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
